import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MessageCircle, Search } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/labels";
import { PageHero } from "./PageHero";
import { SJI_WA_NUMBER } from "../../components/public/ContactSJI";

const waLink = (code, label) => `https://wa.me/${SJI_WA_NUMBER}?text=${encodeURIComponent(
  `Halo PT SJI, saya ingin menanyakan pengajuan Pemodal Kebun saya.\nKode pengajuan: ${code || "-"}${label ? `\nStatus saat ini: ${label}` : ""}`)}`;

const WaAsk = ({ code, label, testid }) => (
  <a href={waLink(code, label)} target="_blank" rel="noopener noreferrer" data-testid={testid}
    className="inline-flex items-center gap-2 rounded-md bg-[#128C7E] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#0e6f64]">
    <MessageCircle className="h-4 w-4" />Tanya PT SJI via WhatsApp
  </a>
);

const TONE = { DITOLAK: "bg-red-50 text-red-800 border-red-200", PERLU_PERBAIKAN: "bg-orange-50 text-orange-900 border-orange-200", AKUN_AKTIF: "bg-emerald-50 text-emerald-900 border-emerald-200" };

const Result = ({ r }) => (
  <div className="rounded-2xl border border-slate-200/80 bg-white p-5 sm:p-7 space-y-4" data-testid="status-result">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="font-mono text-sm text-slate-600" data-testid="status-code">{r.code}</p>
      <span className={`rounded-full border px-3 py-1 text-xs font-bold uppercase ${TONE[r.status] || "bg-amber-50 text-amber-900 border-amber-200"}`} data-testid="status-label">{r.statusLabel}</span>
    </div>
    <p className="text-sm text-slate-800" data-testid="status-next-step">{r.nextStep}</p>
    {r.status === "AKUN_AKTIF" && <Button asChild className="bg-emerald-800 hover:bg-emerald-900"><Link to="/login" data-testid="status-login-link">Masuk</Link></Button>}
    <div className="flex flex-wrap items-center gap-3"><WaAsk code={r.code} label={r.statusLabel} testid="status-wa-btn" />
      <span className="text-xs text-slate-500">Kode pengajuan sudah terisi otomatis di pesan.</span></div>
    <ol className="border-l-2 border-emerald-100 pl-4 space-y-3" data-testid="status-history">
      {r.history.map((h, i) => (
        <li key={`${h.at}-${i}`} className="text-sm"><p className="font-semibold text-slate-800">{h.label}</p><p className="text-xs text-slate-500">{formatDate(h.at)}</p></li>
      ))}
    </ol>
  </div>
);

export default function InvestorStatusPage() {
  const [f, setF] = useState({ code: "", email: "" });
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setRes(null); setBusy(true);
    try { setRes((await api.post("/public/investor-applications/status", f)).data); }
    catch (x) { setErr(x?.response?.status === 422 ? "Isi kode pengajuan dan email yang valid." : formatApiError(x)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <PageHero eyebrow="Calon Pemodal" title="Cek Status Pengajuan" intro="Masukkan kode pengajuan (PMD-...) dari email konfirmasi dan email yang Anda pakai saat mendaftar." />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-6">
        <form onSubmit={submit} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end bg-white border border-slate-200/80 rounded-2xl p-5" data-testid="status-form">
          <label className="text-sm"><span className="font-semibold text-slate-700">Kode pengajuan</span>
            <Input required placeholder="PMD-20261025-AB12" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} className="h-11 mt-1 font-mono uppercase" data-testid="status-code-input" /></label>
          <label className="text-sm"><span className="font-semibold text-slate-700">Email</span>
            <Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-11 mt-1" data-testid="status-email-input" /></label>
          <Button type="submit" disabled={busy} className="h-11 bg-emerald-800 hover:bg-emerald-900" data-testid="status-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4 mr-1.5" />}Cek
          </Button>
        </form>
        {err && <div className="space-y-3"><p className="text-sm text-red-700" data-testid="status-error">{err}</p>
          <p className="text-sm text-slate-600">Lupa kode atau tidak menemukan pengajuan? <WaAsk code={f.code.trim().toUpperCase()} testid="status-error-wa-btn" /></p></div>}
        {res && <Result r={res} />}
        <p className="text-xs text-slate-500">Belum mengajukan? <Link to="/ingin-menjadi-pemodal" className="font-semibold text-emerald-800 underline" data-testid="status-apply-link">Isi formulir Ingin Menjadi Pemodal</Link></p>
      </div>
    </>
  );
}
